import { Globals } from '@/modules/Globals';
import { AssistantService } from '@/api/services/assistant.service';
import type {
  IAssistantProposalView,
  IAssistantRow,
} from '@/interfaces/assistant.interface';
import { stillHiddenReasons } from '../cards/availability-context';
import type { IProposalCardInput } from '../cards/proposal-cards';
import { rowsTickedByDefault } from '../cards/row-requirements';
import { firstSizeChangedIds } from '../cards/sizes-context';
import type { ICartadigitalAdminHostToolDeps } from '../tools/tool-types';

type TProposalContextDeps = Pick<
  ICartadigitalAdminHostToolDeps,
  'restaurantStore' | 'menuStore'
>;

/**
 * By row key, what still hides from customers here something each of `rows`
 * turns on (plan P0-3, P2-4; the proposal card and the receipt), with the
 * categories as the rows that apply (`applying`) leave them. Rows without
 * reasons are absent.
 */
export function stillHiddenByRow(
  deps: TProposalContextDeps,
  rows: readonly IAssistantRow[],
  applying: readonly IAssistantRow[] = rows
): IProposalCardInput['stillHidden'] {
  const stillHidden: IProposalCardInput['stillHidden'] = {};
  for (const row of rows) {
    const reasons = stillHiddenReasons(row, deps, applying);
    if (reasons.length) stillHidden[row.key] = reasons;
  }
  return stillHidden;
}

/**
 * The card input of a proposal view in the current store (tool and card
 * actions). What still hides each row's subject is read as «Aplicar» leaves
 * the categories as the card first shows it: flagged rows, and the rows that
 * need one, start unticked. Which products' price follows another of their
 * sizes is read from every row (plan P4-1).
 */
export function proposalCardInput(
  deps: TProposalContextDeps,
  view: IAssistantProposalView,
  requestText: string | null
): IProposalCardInput {
  return {
    view,
    requestText,
    multiBranch: (deps.restaurantStore.branches?.length ?? 0) > 1,
    uploadsPath: Globals.uploadsPath,
    stillHidden: stillHiddenByRow(
      deps,
      view.rows,
      rowsTickedByDefault(view.rows)
    ),
    firstSizeChangedIds: firstSizeChangedIds(deps, view.rows),
  };
}

/**
 * Discards a proposal left with nothing to apply (tool and card actions): a
 * refresh or a stale apply can leave it `proposed` without rows, and it
 * would hold the member's pending slot until it expires. Best effort: the
 * API expires it anyway.
 */
export async function discardEmptyProposal(
  restaurantId: number,
  proposalId: string
): Promise<void> {
  try {
    await AssistantService.discard(restaurantId, proposalId);
  } catch (error) {
    console.warn('[assistant] empty proposal not discarded', error);
  }
}
